import {
    DestroyRef,
    Directive,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {Router} from '@angular/router';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {AlertService} from '../../../sdk/service/alert.service';
import {AuthenticationService} from '../../../sdk/service/authentication.service';
import {BusinessType, PublicService} from 'web-angular-client';
import {SignupDetail} from '../model/signup-detail';
import {EnvironmentService} from '../../../sdk/service/environment.service';
import {PrefilledSignupForm} from '../prefilled-signup-form';

/**
 * Shared behaviour for the two Step 1 variants. Holds the inputs/outputs, prefill handling, submit emission and the
 * common signup-detail extraction; each concrete variant supplies its own form definition via {@link buildForm} so
 * neither template carries mode branches.
 */
@Directive()
export abstract class SignupStep1BaseComponent extends PrefilledSignupForm<SignupDetail>
    implements OnInit, OnDestroy, OnChanges
{
    @Output()
    signupEvent: EventEmitter<SignupDetail>;

    @Output()
    nextPage: EventEmitter<number>;

    @Input()
    isIntegratorSignup!: boolean;

    @Input()
    prefilledSignupDetail: SignupDetail;

    @Input()
    isMerchantSelfServiceEnabled: boolean;

    private readonly _serviceTermsLink: string;
    private readonly _privacyNoticeLink: string;

    protected constructor(
        protected router: Router,
        protected formBuilder: UntypedFormBuilder,
        protected alertService: AlertService,
        protected authenticationService: AuthenticationService,
        protected publicService: PublicService,
        protected environmentService: EnvironmentService,
        protected readonly destroyRef: DestroyRef,
    )
    {
        super(router, formBuilder, alertService, authenticationService, environmentService);
        this.signupEvent = new EventEmitter();
        this.nextPage = new EventEmitter();

        this._serviceTermsLink = this.environmentService.serviceTermsLink;
        this._privacyNoticeLink = this.environmentService.privacyNoticeLink;
    }

    get serviceTermsLink(): string
    {
        return this._serviceTermsLink;
    }

    get privacyNoticeLink()
    {
        return this._privacyNoticeLink || this.serviceTermsLink;
    }

    get privacyNoticeLabel(): string
    {
        return this.environmentService.privacyNoticeLabel;
    }

    get serviceTermsLabel(): string
    {
        return this.environmentService.serviceTermsLabel;
    }

    /**
     * Whether the user has ticked the checkbox accepting the privacy and service terms.
     */
    get termsAccepted(): boolean
    {
        return this.form?.get('termsAccepted')?.value === true;
    }

    get instanceName()
    {
        return this.environmentService.instanceName;
    }

    get businessType(): BusinessType
    {
        return this.isIntegratorSignup ? BusinessType.PTY : BusinessType.SOLE_PROPRIETOR;
    }

    ngOnInit(): void
    {
        this.form = this.buildForm();
        this.afterFormBuilt();
    }

    ngOnChanges(changes: SimpleChanges): void
    {
        this.loadPrefilledDetails(changes);

        if (this.prefilledSignupDetail)
        {
            this.patchPrefilledDataToForm(this.prefilledSignupDetail);
        }
    }

    ngOnDestroy(): void
    {
        this.signupEvent.unsubscribe();
        this.nextPage.unsubscribe();
    }

    loadPrefilledDetails(changes: SimpleChanges): void
    {
        if (changes['prefilledSignupDetail'] && changes['prefilledSignupDetail'].currentValue)
        {
            this.prefilledSignupDetail = changes['prefilledSignupDetail'].currentValue as SignupDetail;
        }
    }

    patchPrefilledDataToForm(prefilledData: SignupDetail)
    {
        if (!this.form || !prefilledData)
        {
            return;
        }

        this.form.patchValue({
            ...prefilledData,
            mobileNumber: prefilledData?.mobileNumber,
            businessType: prefilledData?.businessType ?? BusinessType.SOLE_PROPRIETOR,
            confirmPassword: prefilledData?.password,
            confirmEmail: prefilledData?.email,
            marketingConsent: this.isMerchantSelfServiceEnabled ? prefilledData?.marketingConsent : false,
            termsAccepted: false
        });
    }

    shouldDisableBusinessType(): boolean
    {
        return this.isIntegratorSignup;
    }

    submit()
    {
        const signupDetail: SignupDetail = this.getSignupDetailFromForm();
        this.signupEvent.emit(signupDetail);
        this.nextPage.emit(2);
    }

    getBusinessNameTooltip(): string
    {
        return 'The registered legal name of the business.';
    }

    getSignUpFormControl(controlKey: keyof SignupDetail): AbstractControl<any, any>
    {
        return this.form.controls[controlKey];
    }

    protected abstract buildForm(): UntypedFormGroup;

    protected afterFormBuilt(): void
    {
    }

    protected getNatureOfBusiness(): string | undefined
    {
        return undefined;
    }

    protected getSignupDetailFromForm(): SignupDetail
    {
        return {
            name: this.getSignUpFormControl('name').value,
            surname: this.getSignUpFormControl('surname').value,
            businessName: this.getSignUpFormControl('businessName').value,
            businessType: this.getSignUpFormControl('businessType').value,
            dateTermsAccepted: new Date(),
            email: this.getSignUpFormControl('email').value,
            mobileNumber: this.getSignUpFormControl('mobileNumber').value,
            password: this.getSignUpFormControl('password').value,
            clientBase: this.getSignUpFormControl('clientBase').value,
            averageTransactionValue: this.getSignUpFormControl('averageTransactionValue').value,
            marketingConsent: this.getSignUpFormControl('marketingConsent').value,
            businessDisplayName: this.getSignUpFormControl('businessDisplayName').value,
            natureOfBusiness: this.getNatureOfBusiness()
        } as SignupDetail;
    }
}
